import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

import EntityFormPage, { EntityForm } from '@entity/EntityForm/EntityForm';
import getGraphqlData from '@boilerplate/lib/getGraphqlData';
import { BaseFormUpdateProps } from '@boilerplate/types/entity';
import { addEntityToRelatedDataOptions } from '@boilerplate/lib/relatedDataToOptions';
import Notistack from '@/lib/notistack';
import PlannedHourEntity from '@/entities/plannedHour';
import { catchGraphQlError } from '@/lib/catchError';

  import ProjectEntity from '@/entities/project';
  import UserEntity from '@/entities/user';

function PlannedHourBaseUpdateForm({ formOnly = false, onClose, Entity = PlannedHourEntity, id: propId, ...props }: BaseFormUpdateProps<typeof PlannedHourEntity.model>) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const id = propId || paramId;

  const Component = formOnly ? EntityForm : EntityFormPage;

  const { loading, data } = PlannedHourEntity.model.useGet({
    variables: {
      id,
            withProject: true,
            withUser: true,
    },
  });

  const [updateMutation, { loading: updateLoading }] = PlannedHourEntity.model.useUpdate();

  const relationsOptionsQuery = PlannedHourEntity.model.useRelationsOptions({
    variables: {
          withProjects: true,
          withUsers: true,
    }
  });

  const relationsOptions = useMemo(
    () =>
      addEntityToRelatedDataOptions(relationsOptionsQuery, [
              ProjectEntity,
              UserEntity,
      ]),
    [relationsOptionsQuery]
  );

  const handleSubmit = (newData) => {
    newData.variables = getGraphqlData(Entity, newData.variables, 'update');

    updateMutation(newData)
      .then((result) => {
        Notistack.toast(t('crud:updatedItem', { item: t('entities:plannedHour') }), { variant: 'success' });

        if (!formOnly) {
          navigate(-1);
        }

        return onClose?.(result.data?.updatePlannedHour);
      })
      .catch(catchGraphQlError);
  };

  if (loading || relationsOptionsQuery.loading) {
    return <CircularProgress />;
  }

  return (
    <Component
      {...props}
      type="update"
      Entity={Entity}
      loading={updateLoading}
      handleSubmit={handleSubmit}
      relationsOptions={relationsOptions}
      data={data.plannedHour}
    />
  );
}

export default PlannedHourBaseUpdateForm;
